import { DownloadConfigInfoType } from '../types';
import { getMetricsItemFunc } from '@op/components/FilterCommon/widgets/MetricsSelect/config';
import { commonRenderHelperMap } from '../renderHelper/common';
import { formatFilterTime } from '../renderHelper/time';
import { tableColumnsList } from '@op/pages/Compare/ChannelShareRanking/constants';
import {
  getCompareChannelShareRankingDownload,
  getCompareCompetitorsGrouping,
} from '@op/services';
import { initialUpperCase, transAbbreviation } from '@op/utils';
import { dealwithTableData } from '@op/pages/Compare/ChannelShareRanking/hooks';

export const ComparisonGameTable: DownloadConfigInfoType = {
  columns: ({ filter, gameInfo, auxiliaryParams }) => {
    const { metrics } = filter;
    const { gameData, dateType } = auxiliaryParams;
    const getMetricsItem = getMetricsItemFunc(gameInfo?.entity_type as any);

    const columns = metrics.map((key: string) => {
      const { text, text_en, platform } = getMetricsItem(key);
      const preTitle = platform ? `${platform}-` : '';

      return {
        key,
        header: preTitle + (text_en || text),
        ...commonRenderHelperMap[key],
      };
    });

    const renderTime = formatFilterTime({ date_type: dateType });

    const tableColumns: any = [
      {
        key: 'edition_unified_id',
        header: 'Game',
        fixed: 'left',
        renderHelper: [
          (item: any, key: string) => {
            const value = item[key];

            return gameData?.[value].entity_name;
          },
        ],
      },
      {
        key: 'start_time',
        header: 'Start Time',
        fixed: 'left',
        renderHelper: [renderTime],
      },
      {
        key: 'end_time',
        header: 'End Time',
        fixed: 'left',
        renderHelper: [renderTime],
      },
      ...columns,
    ];

    return tableColumns;
  },
};

export const ComparisonGameChart: DownloadConfigInfoType = {
  columns: ({ gameInfo, auxiliaryParams }) => {
    const { key, dateType } = auxiliaryParams;
    const getMetricsItem = getMetricsItemFunc(gameInfo?.entity_type as any);
    const { text, text_en, platform } = getMetricsItem(key);
    const preTitle = platform ? `${platform}-` : '';

    const renderTime = formatFilterTime({ date_type: dateType });

    const tableColumns: any = [
      {
        key: 'name',
        header: 'Game',
        fixed: 'left',
      },
      {
        key: 'time',
        header: 'Time',
        fixed: 'left',
        renderHelper: [renderTime],
      },
      {
        key: 'value',
        header: preTitle + (text_en || text),
        ...commonRenderHelperMap[key],
      },
    ];

    return tableColumns;
  },
};

export const CompareChannelShareRankingTable: DownloadConfigInfoType = {
  columns: () => {
    const list = tableColumnsList
      .map(({ value, text, isPercentage }: any) => {
        const obj: any = {
          key: value,
          header: text,
          numFmt: '0',
        };
        if (isPercentage) {
          obj.numFmt = '0.00%';
        }
        return [
          obj,
          {
            key: `${value}_qoq`,
            header: `Change% of ${text}`,
            numFmt: '0.00%',
            renderHelper: [
              (item: any, key: string) => {
                return (item[key] || 0) / 100;
              },
            ],
          },
        ];
      })
      .flat(2);

    const tableColumns: any = [
      {
        key: 'entity_name',
        header: 'Game',
        fixed: 'left',
      },
      {
        key: 'entity_type',
        header: 'Platform',
        fixed: 'left',
      },
      ...list,
    ];
    return tableColumns;
  },
};

export const CompareChannelShareRankingTableTotal: DownloadConfigInfoType = {
  sheetNames: [
    { name: 'Total', key: 'total', addOn: '', replaceName: '' },
    { name: 'Youtube', key: 'youtube', addOn: '', replaceName: '' },
    { name: 'Tiktok', key: 'tiktok', addOn: '', replaceName: '' },
    { name: 'Instagram', key: 'instagram', addOn: '', replaceName: '' },
    { name: 'Facebook', key: 'facebook', addOn: '', replaceName: '' },
    { name: 'X', key: 'twitter', addOn: '', replaceName: '' },
  ],
  columns: () => {
    const list = tableColumnsList
      .map(({ value, text, isPercentage }: any) => {
        const obj: any = {
          key: value,
          header: text,
          numFmt: '0',
        };
        if (isPercentage) {
          obj.numFmt = '0.00%';
        }
        return [
          obj,
          {
            key: `${value}_qoq`,
            header: `Change% of ${text}`,
            numFmt: '0.00%',
            renderHelper: [
              (item: any, key: string) => {
                return (item[key] || 0) / 100;
              },
            ],
          },
        ];
      })
      .flat(2);

    const tableColumns: any = [
      {
        key: 'entity_name',
        header: 'Game',
        fixed: 'left',
      },
      {
        key: 'entity_type',
        header: 'Platform',
        fixed: 'left',
      },
      ...list,
    ];
    return tableColumns;
  },
  customizeDownloadFunc: async ({ filter, auxiliaryParams }) => {
    const { data } = auxiliaryParams;
    const { result_per_channel } = await getCompareChannelShareRankingDownload(
      filter,
    );
    const channelList: any = {
      youtube: [],
      tiktok: [],
      facebook: [],
      twitter: [],
      instagram: [],
    };

    Object.keys(result_per_channel).forEach((channel) => {
      const val = result_per_channel[channel];
      const { list } = val || {};
      const sortedList = [
        ...list.filter(
          (item: { game: any }) =>
            item.game.edition_unified_id === filter.edition_unified_id,
        ),
        ...list.filter(
          (item: { game: any }) =>
            item.game.edition_unified_id !== filter.edition_unified_id,
        ),
      ];
      sortedList.forEach((each: any) => {
        const { list, old_list, game } = each;
        const data = dealwithTableData(list, old_list);
        if (!channelList[channel]) {
          channelList[channel] = [];
        }
        channelList[channel].push({
          ...data,
          ...game,
        });
      });
    });

    return {
      total: data,
      ...channelList,
    };
  },
};

export const ComparisonGameTableWithGroup: DownloadConfigInfoType = {
  columns: ({ auxiliaryParams }) => {
    const { gameData, dateType, groupBy } = auxiliaryParams;
    const groupByHeader = initialUpperCase(groupBy);

    const columns = Array.from({ length: 3 }, (_, index) => {
      const top = index + 1;
      return [
        {
          key: `key_${top}`,
          header: `TOP ${groupByHeader} ${top}`,
        },
        {
          key: `value_${top}`,
          header: `TOP ${groupByHeader} ${top} Value`,
          cellFormat: true,
        },
      ];
    }).flat();

    const renderTime = formatFilterTime({ date_type: dateType });

    const tableColumns: any = [
      {
        key: 'edition_unified_id',
        header: 'Game',
        fixed: 'left',
        renderHelper: [
          (item: any, key: string) => {
            const value = item[key];

            return gameData?.[value].entity_name;
          },
        ],
      },
      {
        key: 'start_time',
        header: 'Start Time',
        fixed: 'left',
        renderHelper: [renderTime],
      },
      {
        key: 'end_time',
        header: 'End Time',
        fixed: 'left',
        renderHelper: [renderTime],
      },
      {
        key: 'metrics',
        header: 'Metrics',
        fixed: 'left',
      },
      {
        key: 'total',
        header: 'Total Value',
        fixed: 'left',
        cellFormat: true,
      },
      ...columns,
    ];

    return tableColumns;
  },
  customizeDownloadFunc: async ({ filter, gameInfo, auxiliaryParams }) => {
    const { metrics } = filter;
    const { data, groupBy } = auxiliaryParams;
    const getMetricsItem = getMetricsItemFunc(gameInfo?.entity_type as any);

    const { grouping_list } = await getCompareCompetitorsGrouping({
      ...filter,
      group_by: groupBy,
    });
    const transKey: any = {
      language: 'lang_en',
      region: 'region_en',
      country: 'country_en',
    };

    const excelData = grouping_list
      .sort((a: { index: number }, b: { index: number }) => a.index - b.index)
      .flatMap((val: any) => {
        const { game_id, list, start_time, end_time } = val;
        return metrics.map((key: string) => {
          const items = list?.[key] || [];
          const { format } = commonRenderHelperMap?.[key] || {};
          const { text, text_en, platform } = getMetricsItem(key);
          const preTitle = platform ? `${platform}-` : '';
          const total = data?.find((val) => val.game_id === game_id)?.[key];
          const row: Record<string, any> = {
            edition_unified_id: game_id,
            start_time,
            end_time,
            metrics: preTitle + (text_en || text),
            total: format?.(total) || total,
          };

          items.forEach(
            (item: { value: number; name: string }, index: number) => {
              const top = index + 1;
              let { name } = item;
              if (transKey?.[groupBy]) {
                if (groupBy === 'region') {
                  name = name.toLocaleUpperCase();
                }
                name = transAbbreviation(name, transKey[groupBy]);
              }
              row[`key_${top}`] = name;
              row[`value_${top}`] = format?.(item.value) || item.value;
            },
          );

          return row;
        });
      });
    return excelData;
  },
};
